import { ArrowPathIcon, InformationCircleIcon } from '@heroicons/react/24/outline';

// Shared furniture for the rebuilt Power BI pages.
//
// The six pages differ in what they count, not in how they are read: a month
// picker, a row of headline figures, a breakdown or two, and an honest note
// about what the numbers could not cover. Keeping that in one place is what
// makes them feel like one report rather than six screens.
export const BRAND = '#711C91';

export const num = (v) => Number(v || 0).toLocaleString('en-GB');
export const dec = (v, d = 1) => Number(v || 0).toLocaleString('en-GB', {
  minimumFractionDigits: d, maximumFractionDigits: d,
});
export const pct = (v) => `${(Number(v || 0) * 100).toFixed(1)}%`;
export const gbp = (v) => `£${Number(v || 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;
export const mins = (v) => (v === null || v === undefined
  ? '—' : `${Math.floor(v / 60)}:${String(Math.round(v % 60)).padStart(2, '0')}`);
export const thisMonth = () => new Date().toISOString().slice(0, 7);
export const monthName = (m) => {
  if (!m) return '';
  const [y, mm] = String(m).split('-').map(Number);
  return new Date(Date.UTC(y, mm - 1, 1)).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
};
export const shortDate = (d) => (d
  ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '—');

export const selectClass = 'rounded-md border border-gray-300 px-2.5 py-1.5 text-sm '
  + 'focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand';

export function PageHeader({
  title, subtitle, month, onMonth, children, onRefresh, fetching,
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 className="text-xl font-bold text-gray-900">{title}</h1>
        <p className="text-sm text-gray-500">{subtitle}</p>
      </div>
      <div className="ml-auto flex flex-wrap items-center gap-2">
        {onMonth ? (
          <input type="month" value={month} onChange={(e) => onMonth(e.target.value)} className={selectClass} />
        ) : null}
        {children}
        <button
          type="button"
          onClick={onRefresh}
          className="rounded-md border border-gray-300 p-1.5 text-gray-500 hover:border-brand"
          title="Refresh"
        >
          <ArrowPathIcon className={`h-4 w-4 ${fetching ? 'animate-spin' : ''}`} />
        </button>
      </div>
    </div>
  );
}

export function Stat({
  label, value, sub, accent, tone,
}) {
  return (
    <div
      className={`rounded-lg border bg-white p-3 ${accent ? 'border-brand' : 'border-gray-200'}`}
    >
      <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">{label}</p>
      <p className="mt-1 text-2xl font-bold tabular-nums" style={{ color: tone || '#111827' }}>{value}</p>
      {sub ? <p className="text-xs text-gray-400">{sub}</p> : null}
    </div>
  );
}

export function Note({ tone = 'amber', children }) {
  const tones = {
    amber: 'border-amber-200 bg-amber-50 text-amber-800',
    red: 'border-red-300 bg-red-50 text-red-800',
    brand: 'border-brand/20 bg-brand-light text-brand-dark',
  };
  const icon = { amber: 'text-amber-600', red: 'text-red-600', brand: 'text-brand' };
  return (
    <div className={`mt-4 flex items-start gap-2 rounded-lg border p-3 ${tones[tone]}`}>
      <InformationCircleIcon className={`mt-0.5 h-4 w-4 shrink-0 ${icon[tone]}`} />
      <div className="text-xs">{children}</div>
    </div>
  );
}

export function Panel({ title, right, children }) {
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2">
        <h2 className="text-sm font-semibold text-gray-700">{title}</h2>
        {right}
      </div>
      {children}
    </div>
  );
}

// A share-of-total row. Used wherever the report drew a bar chart of counts.
export function BarRow({
  label, value, total, sub, colour = BRAND,
}) {
  const w = total ? Math.max(Number(value) / total, 0) : 0;
  return (
    <div className="px-4 py-1.5">
      <div className="flex items-baseline justify-between gap-3 text-sm">
        <span className="truncate text-gray-700">{label}</span>
        <span className="shrink-0 tabular-nums text-gray-500">
          {num(value)}
          {sub ? <span className="ml-1 text-xs text-gray-400">{sub}</span> : null}
        </span>
      </div>
      <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
        <div className="h-full rounded-full" style={{ width: `${w * 100}%`, background: colour }} />
      </div>
    </div>
  );
}

// The daily columns every one of these pages wants somewhere.
export function DayChart({ rows, series, height = 120 }) {
  const max = Math.max(1, ...rows.flatMap((r) => series.map((s) => Number(r[s.key] || 0))));
  return (
    <div className="overflow-x-auto px-4 py-3">
      <div className="flex items-end gap-1" style={{ height }}>
        {rows.map((r) => (
          <div key={r.date} className="flex min-w-[10px] flex-1 flex-col justify-end" title={r.date}>
            {series.map((s) => (
              <div
                key={s.key}
                style={{
                  height: `${(Number(r[s.key] || 0) / max) * (height - 18)}px`,
                  background: s.colour,
                }}
                className="w-full first:rounded-t-sm"
              />
            ))}
          </div>
        ))}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-3">
        {series.map((s) => (
          <span key={s.key} className="flex items-center gap-1 text-[11px] text-gray-500">
            <span className="inline-block h-2 w-2 rounded-sm" style={{ background: s.colour }} />
            {s.label}
          </span>
        ))}
        <span className="ml-auto text-[11px] text-gray-400">
          {rows.length ? `${shortDate(rows[0].date)} – ${shortDate(rows[rows.length - 1].date)}` : ''}
        </span>
      </div>
    </div>
  );
}

export function States({ isLoading, isError, what }) {
  if (isError) return <p className="mt-4 text-sm text-red-500">{`Could not load ${what}.`}</p>;
  if (isLoading) return <p className="mt-4 text-sm text-gray-400">Loading…</p>;
  return null;
}
